import React, { useCallback, useEffect, useState } from "react";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { listWalletDeposits } from "../../../api/services/depositDeclarationService";
import { alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { DEPOSIT_HISTORY_REFRESH } from "./depositDeclarationModel";
import { DepositHistoryStatus } from "./DepositSenderConfirm";
import {
  depositHistoryApiParse,
  mapDepHistoryRow,
  orderHistoryClickIgnoreDetail,
  resolveDepositHistoryAddressExplorerHref,
  resolveDepositHistoryTxExplorerHref,
} from "./depositHistoryShared";
import DepositHistoryDetailModal from "./DepositHistoryDetailModal";

/* Filters (restored later)
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import {
  matchesDepositCoin,
  matchesStatusNorm,
  matchesTypeNorm,
  rowWithinPastDays,
} from "./orderFilterHelpers";

const TYPE_OPTIONS = [
  { value: "crypto", label: "Crypto" },
  { value: "fiat", label: "Fiat" },
];
const TIME_OPTIONS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];
const COIN_OPTIONS_FALLBACK = [
  { value: "all", label: "All" },
  { value: "btc", label: "Bitcoin" },
  { value: "eth", label: "Ethereum" },
  { value: "matic", label: "Polygon" },
  { value: "ltc", label: "Litecoin" },
];
const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
];
*/

const ORDER_DESKTOP_COL_COUNT = 7;

const DepHistCopyIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
      fill="currentColor"
    />
  </svg>
);

const DepHistLinkIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5M16.5 3H21m0 0v4.5M21 3 9 15"
    />
  </svg>
);

const DepositHistory = () => {
  const [rows, setRows] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [detailRow, setDetailRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const openDetail = useCallback((row) => {
    setDetailRow(row);
    setDetailOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailRow(null);
  }, []);

  const copyText = useCallback((text, label = "Copied") => {
    const t = text == null ? "" : String(text);
    if (!t || t === "—") return;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(t).then(() => alertSuccessMessage(label));
    }
  }, []);

  const reload = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoadingInitial(true);
    try {
      const res = await listWalletDeposits();
      const { rawRows } = depositHistoryApiParse(res);
      setRows((rawRows || []).map((raw, i) => mapDepHistoryRow(raw, i)));
    } catch {
      if (!silent) setRows([]);
    } finally {
      if (!silent) setLoadingInitial(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onRefresh = () => {
      void reload({ silent: true });
    };
    window.addEventListener(DEPOSIT_HISTORY_REFRESH, onRefresh);
    return () => window.removeEventListener(DEPOSIT_HISTORY_REFRESH, onRefresh);
  }, [reload]);

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  const renderLoadingBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <span className="so-table__td--muted" role="status">
          Loading…
        </span>
      </td>
    </tr>
  );

  const showEmpty = !loadingInitial && rows.length === 0;

  const networkTitle = (row) =>
    row?.chain_full_name && row.chain_full_name !== "—" ? row.chain_full_name : row?.network;
  const networkDisplay = (row) =>
    row?.chain_full_name && row.chain_full_name !== "—" ? row.chain_full_name : row.network;

  const subTabs = [
    { label: "Deposit", to: "/user_profile/orders/deposit_history" },
    { label: "Withdraw", to: "/user_profile/orders/withdraw_history" },
    { label: "Transfer", to: "/user_profile/orders/transfer" },
    // { label: "Referral", to: "/user_profile/orders/referral" },
  ];

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-deposit-history-title">
            <h1 id="so-deposit-history-title" className="so-page__title">
              Deposit History
            </h1>

            <OrdersSubTabs tabs={subTabs} />

            {/* Filters — restore when product re-enables client filters
            {renderToolbar(false, "desk")}
            */}

            <div className="so-tableWrap">
              <table className="so-table so-table--air">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Network</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Deposit Wallet</th>
                    <th scope="col">Address</th>
                    <th scope="col">TxID</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingInitial
                    ? renderLoadingBody()
                    : showEmpty
                      ? renderEmptyBody()
                      : rows.map((row) => {
                        const addressHref = resolveDepositHistoryAddressExplorerHref(row.explorerLink, row.address);
                        const txHref = resolveDepositHistoryTxExplorerHref(row.explorerLink, row.txid);
                        const netTitle = networkTitle(row);
                        return (
                          <tr
                            key={row.id}
                            className="so-orderHist-clickRow"
                            tabIndex={0}
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              if (orderHistoryClickIgnoreDetail(e.target)) return;
                              openDetail(row);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openDetail(row);
                              }
                            }}
                          >
                            <td className="so-num" title={row.date !== "—" ? row.date : undefined}>
                              {row.date}
                            </td>
                            <td title={netTitle && netTitle !== "—" ? netTitle : undefined}>{networkDisplay(row)}</td>
                            <td className="so-num">
                              {row.net_amount} {row.short_name}
                            </td>
                            <td>{row.depositWallet}</td>
                            <td title={row.address !== "—" ? row.address : undefined}>
                              <div className="so-depHist_actions">
                                <span className="so-depHist_trunc">{row.addressShort}</span>
                                {row.address !== "—" ? (
                                  <>
                                    <button
                                      type="button"
                                      className="so-depHist_iconBtn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyText(row.address, "Address copied");
                                      }}
                                      aria-label="Copy address"
                                      title="Copy address"
                                    >
                                      <DepHistCopyIcon />
                                    </button>
                                    {addressHref ? (
                                      <a
                                        className="so-depHist_iconBtn so-depHist_iconBtn--link"
                                        href={addressHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="View address on explorer"
                                        title="View address on explorer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <DepHistLinkIcon />
                                      </a>
                                    ) : null}
                                  </>
                                ) : null}
                              </div>
                            </td>
                            <td title={row.txid !== "—" ? row.txid : undefined}>
                              <div className="so-depHist_actions">
                                <span className="so-depHist_trunc">{row.txidShort}</span>
                                {row.txid !== "—" ? (
                                  <>
                                    <button
                                      type="button"
                                      className="so-depHist_iconBtn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyText(row.txid, "TxID copied");
                                      }}
                                      aria-label="Copy TxID"
                                      title="Copy TxID"
                                    >
                                      <DepHistCopyIcon />
                                    </button>
                                    {txHref ? (
                                      <a
                                        className="so-depHist_iconBtn so-depHist_iconBtn--link"
                                        href={txHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="View transaction on explorer"
                                        title="View transaction on explorer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <DepHistLinkIcon />
                                      </a>
                                    ) : null}
                                  </>
                                ) : null}
                              </div>
                            </td>
                            <td>
                              <DepositHistoryStatus
                                row={row}
                                showConfirmButton={false}
                                badgeClassName={`so-depHist_badge is-${row.actionTone || "neutral"}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Deposit History</h2>
          <OrdersSubTabs tabs={subTabs} />
          {/* <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-deposit-history-filters" ... />
          </div> */}
          {loadingInitial ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <span className="so-table__td--muted">Loading…</span>
            </div>
          ) : showEmpty ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            <>
              {rows.map((row) => {
                const addressHref = resolveDepositHistoryAddressExplorerHref(row.explorerLink, row.address);
                const txHref = resolveDepositHistoryTxExplorerHref(row.explorerLink, row.txid);
                const netLabel = networkDisplay(row);
                return (
                  <OrderMobileCardShell
                    key={`m-${row.id}`}
                    desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
                    popupTitle="Deposit details"
                    externalDetail
                    onOpenExternalDetail={() => openDetail(row)}
                  >
                    <div className="so-mobileCard__row">
                      <span>Date</span>
                      <span className="so-num">{row.date}</span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Network</span>
                      <span>{netLabel}</span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Amount</span>
                      <span className="so-num">
                        {row.net_amount} {row.short_name}
                      </span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Deposit Wallet</span>
                      <span>{row.depositWallet}</span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Address</span>
                      <span className="so-depHist_actions so-depHist_actions--wrap">
                        <span className="so-depHist_trunc">{row.addressShort}</span>
                        {row.address !== "—" ? (
                          <>
                            <button
                              type="button"
                              className="so-depHist_iconBtn"
                              onClick={() => copyText(row.address, "Address copied")}
                              aria-label="Copy address"
                              title="Copy address"
                            >
                              <DepHistCopyIcon />
                            </button>
                            {addressHref ? (
                              <a
                                className="so-depHist_iconBtn so-depHist_iconBtn--link"
                                href={addressHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View address on explorer"
                                title="View address on explorer"
                              >
                                <DepHistLinkIcon />
                              </a>
                            ) : null}
                          </>
                        ) : null}
                      </span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>TxID</span>
                      <span className="so-depHist_actions so-depHist_actions--wrap">
                        <span className="so-depHist_trunc">{row.txidShort}</span>
                        {row.txid !== "—" ? (
                          <>
                            <button
                              type="button"
                              className="so-depHist_iconBtn"
                              onClick={() => copyText(row.txid, "TxID copied")}
                              aria-label="Copy TxID"
                              title="Copy TxID"
                            >
                              <DepHistCopyIcon />
                            </button>
                            {txHref ? (
                              <a
                                className="so-depHist_iconBtn so-depHist_iconBtn--link"
                                href={txHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View transaction on explorer"
                                title="View transaction on explorer"
                              >
                                <DepHistLinkIcon />
                              </a>
                            ) : null}
                          </>
                        ) : null}
                      </span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Status</span>
                      <span>
                        <DepositHistoryStatus
                          row={row}
                          showConfirmButton={false}
                          badgeClassName={`so-depHist_badge is-${row.actionTone || "neutral"}`}
                        />
                      </span>
                    </div>
                  </OrderMobileCardShell>
                );
              })}
            </>
          )}
        </div>
      </div>

      <DepositHistoryDetailModal open={detailOpen} row={detailRow} onClose={closeDetail} />
    </div>
  );
};

export default DepositHistory;
